/**
 * The client journey, from first call to annual review, as one pure
 * function: buildJourney(facts) → Journey. No copy lives here (see
 * journeyCopy.ts), only where the client is and what state each step is in.
 * The SwiftUI app ports this file 1:1 and reuses tests/journey fixtures.
 *
 * Twelve steps in four chapters. Every Welthra application status (19 plus
 * commission_paid), blocker type (14) and policy lifecycle state (5) maps to
 * a status key; internal-only blockers never reach the client.
 */
import type { Appointment, ApplicationFacts, ApplicationStatus, BlockerType, Counteroffer, JourneyFacts, PolicyFacts, Requirement } from "@/data/types";

export const STEPS = [
  "intro_call",
  "strategy_session",
  "plan_presented",
  "pre_application",
  "submitted",
  "underwriting",
  "requirements",
  "decision",
  "issued",
  "first_premium",
  "active",
  "annual_review",
] as const;
export type StepKey = (typeof STEPS)[number];

export const CHAPTERS = ["getting_started", "your_plan", "carrier_review", "your_policy"] as const;
export type ChapterKey = (typeof CHAPTERS)[number];

export const CHAPTER_OF: Record<StepKey, ChapterKey> = {
  intro_call: "getting_started",
  strategy_session: "getting_started",
  plan_presented: "your_plan",
  pre_application: "your_plan",
  submitted: "carrier_review",
  underwriting: "carrier_review",
  requirements: "carrier_review",
  decision: "carrier_review",
  issued: "your_policy",
  first_premium: "your_policy",
  active: "your_policy",
  annual_review: "your_policy",
};

/**
 * done, current (in progress), upcoming, skipped (not needed on the way),
 * stopped (the journey ended here), closed (after a stop: will not happen).
 */
export type StepState = "done" | "current" | "upcoming" | "skipped" | "stopped" | "closed";

export type StatusKey =
  | "intro.not_booked"
  | "intro.booked"
  | "strategy.not_booked"
  | "strategy.booked"
  | "plan.preparing"
  | "plan.ready"
  | "preapp.open"
  | "preapp.received"
  | "submitted"
  | "underwriting.reviewing"
  | "underwriting.requirements_open"
  | "underwriting.requirements_booked"
  | "underwriting.waiting_records"
  | "underwriting.hidden"
  | "decision.approved"
  | "decision.counteroffer"
  | "decision.postponed"
  | "decision.declined"
  | "ended.withdrawn"
  | "ended.not_taken"
  | "ended.free_look"
  | "issued.delivery"
  | "active.in_force"
  | "active.review_booked"
  | "policy.lapsed"
  | "policy.surrendered"
  | "policy.matured"
  | "policy.claim_paid"
  | "missed.call"
  | "closed.not_a_fit"
  | "closed.not_going_ahead"
  | "closed.do_not_contact"
  | "closed.file";

/** Who is doing the next thing. */
export type Actor = "you" | "strategist" | "coordinator" | "carrier" | "exam_company";

/** How the hero should feel. */
export type Tone = "progress" | "attention" | "celebrate" | "paused" | "stopped" | "calm";

export type Moment = "approved" | "issued" | "active";

export interface JourneyStep {
  key: StepKey;
  chapter: ChapterKey;
  state: StepState;
  /** When it happened (done) or is booked for (current/upcoming). */
  at: string | null;
}

export type RequirementKind = "exam" | "phone_interview" | "documents" | "doctor_records" | "labs" | "carrier_items" | "first_premium";
export type RequirementState = "needed" | "scheduled" | "waiting" | "done";

export interface RequirementView {
  id: string;
  kind: RequirementKind;
  state: RequirementState;
  /** Scheduled time (exam, interview) or due date. */
  when: string | null;
  dueAt: string | null;
  items: { label: string; done: boolean }[];
  /** Who acts on it now. */
  actor: Actor;
}

export interface Journey {
  steps: JourneyStep[];
  current: StepKey;
  status: StatusKey;
  tone: Tone;
  actor: Actor;
  /** 0..1, for the hero's progress bar. */
  progress: number;
  requirements: RequirementView[];
  moment: Moment | null;
  nextAppointment: Appointment | null;
  carrierName: string | null;
  productName: string | null;
  /** Dates the copy needs. */
  dates: { postponedUntil: string | null; firstPremiumDue: string | null; reviewAround: string | null; decidedAt: string | null };
  counteroffer: Counteroffer | null;
  /** true when the position comes from the coarse contact stage only. */
  approximate: boolean;
  /** Welthra V2's "Let's find a new time" after a missed or canceled call with nothing booked; null otherwise. */
  recover: { title: string; bookUrl: string | null } | null;
}

/* ------------------------------------------------------------------ */
/* Requirements                                                        */
/* ------------------------------------------------------------------ */

/** Blockers that describe the team's own judgement or logistics: never shown. */
export const INTERNAL_BLOCKERS: ReadonlySet<BlockerType> = new Set(["thinking_it_over", "client_unresponsive", "client_traveling", "other", "awaiting_carrier_decision"]);

export function requirementView(r: Requirement): RequirementView | null {
  if (INTERNAL_BLOCKERS.has(r.type)) return null;
  const done = r.resolved;
  const base = { id: r.id, when: r.scheduledAt ?? null, dueAt: r.dueAt, items: r.items ?? [] };
  switch (r.type) {
    case "medical_exam_needed":
      return { ...base, kind: "exam", state: done ? "done" : "needed", actor: "you" };
    case "medical_exam_scheduled":
      return { ...base, kind: "exam", state: done ? "done" : "scheduled", actor: "exam_company" };
    case "telephone_interview_needed":
      return { ...base, kind: "phone_interview", state: done ? "done" : "needed", actor: "you" };
    case "telephone_interview_scheduled":
      return { ...base, kind: "phone_interview", state: done ? "done" : "scheduled", actor: "carrier" };
    case "documents_requested": {
      const allDone = done || (base.items.length > 0 && base.items.every((i) => i.done));
      return { ...base, kind: "documents", state: allDone ? "done" : "needed", actor: "you" };
    }
    case "aps_pending":
      return { ...base, kind: "doctor_records", state: done ? "done" : "waiting", actor: "carrier" };
    case "labs_pending":
      return { ...base, kind: "labs", state: done ? "done" : "waiting", actor: "carrier" };
    case "carrier_requirements_outstanding":
      return { ...base, kind: "carrier_items", state: done ? "done" : "needed", actor: "strategist" };
    case "awaiting_first_premium":
      return { ...base, kind: "first_premium", state: done ? "done" : "needed", actor: "you" };
    default:
      return null;
  }
}

const REQUIREMENT_ORDER: RequirementKind[] = ["documents", "exam", "phone_interview", "carrier_items", "doctor_records", "labs", "first_premium"];
const STATE_ORDER: RequirementState[] = ["needed", "scheduled", "waiting", "done"];

export function sortRequirements(list: RequirementView[]): RequirementView[] {
  return [...list].sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state) || REQUIREMENT_ORDER.indexOf(a.kind) - REQUIREMENT_ORDER.indexOf(b.kind));
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const ACTIVE_APPT = new Set(["scheduled", "confirmed"]);

function upcoming(appts: Appointment[], now: number): Appointment[] {
  return appts.filter((a) => ACTIVE_APPT.has(a.status) && new Date(a.endsAt).getTime() >= now).sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

function latestDone(appts: Appointment[], kind: Appointment["kind"], now: number): Appointment | null {
  const done = appts
    .filter((a) => a.kind === kind && (a.status === "completed" || ((a.status === "scheduled" || a.status === "confirmed") && new Date(a.endsAt).getTime() < now)))
    .sort((a, b) => b.startsAt.localeCompare(a.startsAt));
  return done[0] ?? null;
}

/** Who's on it when everything is booked: whoever the soonest booking is with (the examiner, or the carrier for a phone interview). */
function bookedActor(booked: RequirementView[]): Actor {
  const soonest = [...booked].sort((a, b) => (a.when ?? "9999").localeCompare(b.when ?? "9999"))[0];
  return soonest?.actor ?? "exam_company";
}

/** Who's on it when things are open: you, if any of them is yours; otherwise whoever owns the first. */
function openActor(open: RequirementView[]): Actor {
  return open.some((r) => r.actor === "you") ? "you" : (open[0]?.actor ?? "you");
}

function addMonths(iso: string, months: number): string {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + months);
  return d.toISOString();
}

type Position = { current: StepKey; status: StatusKey; stoppedAt?: StepKey; approximate?: boolean };

/**
 * Position from the application status alone. An unknown status (Welthra
 * added one this app doesn't know yet) returns null, and the caller falls
 * back to the contact stage rather than guessing.
 */
export function positionForApplication(status: ApplicationStatus, hasOpenRequirements: boolean, hasBookedOnly: boolean, waitingOnly: boolean): Position | null {
  switch (status) {
    case "draft":
    case "ready_to_submit":
      return { current: "submitted", status: "preapp.received" };
    case "submitted_to_carrier":
      return { current: "submitted", status: "submitted" };
    case "in_underwriting":
      if (hasOpenRequirements) return { current: "requirements", status: "underwriting.requirements_open" };
      if (hasBookedOnly) return { current: "requirements", status: "underwriting.requirements_booked" };
      if (waitingOnly) return { current: "requirements", status: "underwriting.waiting_records" };
      return { current: "underwriting", status: "underwriting.reviewing" };
    case "requirements_outstanding":
      if (hasOpenRequirements) return { current: "requirements", status: "underwriting.requirements_open" };
      if (hasBookedOnly) return { current: "requirements", status: "underwriting.requirements_booked" };
      if (waitingOnly) return { current: "requirements", status: "underwriting.waiting_records" };
      return { current: "requirements", status: "underwriting.hidden" };
    case "medical_exam_scheduled":
      return { current: "requirements", status: hasOpenRequirements ? "underwriting.requirements_open" : "underwriting.requirements_booked" };
    case "approved_as_applied":
      return { current: "issued", status: "decision.approved" };
    case "approved_other_than_applied":
      return { current: "decision", status: "decision.counteroffer" };
    case "postponed":
      return { current: "decision", status: "decision.postponed" };
    case "declined":
      return { current: "decision", status: "decision.declined", stoppedAt: "decision" };
    case "withdrawn":
      return { current: "decision", status: "ended.withdrawn", stoppedAt: "decision" };
    case "issued":
      return { current: "first_premium", status: "issued.delivery" };
    case "not_taken":
      return { current: "issued", status: "ended.not_taken", stoppedAt: "issued" };
    case "cancelled_during_free_look":
      return { current: "active", status: "ended.free_look", stoppedAt: "active" };
    case "placed_in_force":
    case "commission_paid":
      return { current: "annual_review", status: "active.in_force" };
    case "lapsed":
      return { current: "active", status: "policy.lapsed" };
    case "surrendered":
      return { current: "active", status: "policy.surrendered", stoppedAt: "active" };
    case "matured":
      return { current: "active", status: "policy.matured", stoppedAt: "active" };
    case "claim_paid":
      return { current: "active", status: "policy.claim_paid", stoppedAt: "active" };
    default:
      return null;
  }
}

/**
 * Where a withdrawal stops the journey: before submission nothing reached the
 * carrier, so Submitted never happened; during review the decision never came.
 */
export function withdrawnStop(app: Pick<ApplicationFacts, "submittedAt" | "approvedAt">): StepKey {
  if (!app.submittedAt) return "submitted";
  if (app.approvedAt) return "issued";
  return "underwriting";
}

/** Contact stages that close the file (or pause it) on the client's side. Calm, never alarming. */
const CLOSED_STAGES: Record<string, StatusKey> = {
  disqualified: "closed.not_a_fit",
  not_interested: "closed.not_going_ahead",
  lost_proposal: "closed.not_going_ahead",
  dnc: "closed.do_not_contact",
  deceased: "closed.file",
};

/** Applications still on their way to a policy (or just decided): newer than an old policy, they lead. */
const OPEN_APPLICATION: ReadonlySet<ApplicationStatus> = new Set([
  "draft",
  "ready_to_submit",
  "submitted_to_carrier",
  "in_underwriting",
  "requirements_outstanding",
  "medical_exam_scheduled",
  "approved_as_applied",
  "approved_other_than_applied",
  "postponed",
  "declined",
  "withdrawn",
]);

/**
 * Whichever is newer leads: an old surrendered policy must not hide a new
 * application in review, and a new application for a second policy shows its
 * progress. An application that became this policy never outranks it.
 */
export function applicationIsNewer(app: ApplicationFacts, policy: PolicyFacts): boolean {
  if (!OPEN_APPLICATION.has(app.status)) return false;
  const appAt = app.statusChangedAt ?? app.submittedAt;
  const policyAt = policy.effectiveDate ?? policy.issuedAt;
  if (!appAt) return false;
  if (!policyAt) return policy.lifecycle === "lapsed" || policy.lifecycle === "surrendered" || policy.lifecycle === "claim_paid";
  return new Date(appAt).getTime() > new Date(policyAt).getTime();
}

function policyPosition(policy: PolicyFacts, reviewBooked: boolean): Position {
  switch (policy.lifecycle) {
    case "active":
      return { current: "annual_review", status: reviewBooked ? "active.review_booked" : "active.in_force" };
    case "pending_first_premium":
      return { current: "first_premium", status: "issued.delivery" };
    case "lapsed":
      return { current: "active", status: "policy.lapsed" };
    case "surrendered":
      return { current: "active", status: "policy.surrendered", stoppedAt: "active" };
    case "claim_paid":
      return { current: "active", status: "policy.claim_paid", stoppedAt: "active" };
  }
}

/** Coarse position from contacts.stage, used when finer facts are hidden. */
export function positionForContactStage(stage: string | null): Position | null {
  switch (stage) {
    case "intro_scheduled":
      return { current: "intro_call", status: "intro.booked", approximate: true };
    case "qualified":
      return { current: "strategy_session", status: "strategy.not_booked", approximate: true };
    case "illustration_created":
      return { current: "plan_presented", status: "plan.preparing", approximate: true };
    case "illustration_presented":
      return { current: "pre_application", status: "plan.ready", approximate: true };
    case "app_submitted":
      return { current: "underwriting", status: "underwriting.hidden", approximate: true };
    case "approved":
      return { current: "issued", status: "decision.approved", approximate: true };
    case "issued":
      return { current: "first_premium", status: "issued.delivery", approximate: true };
    case "issued_paid":
    case "active":
    case "renewing":
      return { current: "annual_review", status: "active.in_force", approximate: true };
    case "declined":
      return { current: "decision", status: "decision.declined", stoppedAt: "decision", approximate: true };
    case "withdrawn":
      return { current: "decision", status: "ended.withdrawn", stoppedAt: "decision", approximate: true };
    case "nta":
      return { current: "issued", status: "ended.not_taken", stoppedAt: "issued", approximate: true };
    case "lapsed":
      return { current: "active", status: "policy.lapsed", approximate: true };
    case "surrendered":
      return { current: "active", status: "policy.surrendered", stoppedAt: "active", approximate: true };
    case "claim_paid":
      return { current: "active", status: "policy.claim_paid", stoppedAt: "active", approximate: true };
    default:
      return null;
  }
}

const TONE: Record<StatusKey, Tone> = {
  "intro.not_booked": "calm",
  "intro.booked": "progress",
  "strategy.not_booked": "progress",
  "strategy.booked": "progress",
  "plan.preparing": "progress",
  "plan.ready": "progress",
  "preapp.open": "attention",
  "preapp.received": "progress",
  submitted: "progress",
  "underwriting.reviewing": "progress",
  "underwriting.requirements_open": "attention",
  "underwriting.requirements_booked": "progress",
  "underwriting.waiting_records": "progress",
  "underwriting.hidden": "progress",
  "decision.approved": "celebrate",
  "decision.counteroffer": "attention",
  "decision.postponed": "paused",
  "decision.declined": "stopped",
  "ended.withdrawn": "calm",
  "ended.not_taken": "calm",
  "ended.free_look": "calm",
  "issued.delivery": "celebrate",
  "active.in_force": "celebrate",
  "active.review_booked": "progress",
  "policy.lapsed": "attention",
  "policy.surrendered": "calm",
  "policy.matured": "calm",
  "policy.claim_paid": "calm",
  "missed.call": "calm",
  "closed.not_a_fit": "calm",
  "closed.not_going_ahead": "calm",
  "closed.do_not_contact": "calm",
  "closed.file": "calm",
};

const ACTOR: Record<StatusKey, Actor> = {
  "intro.not_booked": "coordinator",
  "intro.booked": "coordinator",
  "strategy.not_booked": "coordinator",
  "strategy.booked": "strategist",
  "plan.preparing": "strategist",
  "plan.ready": "you",
  "preapp.open": "you",
  "preapp.received": "strategist",
  submitted: "carrier",
  "underwriting.reviewing": "carrier",
  "underwriting.requirements_open": "you",
  "underwriting.requirements_booked": "exam_company",
  "underwriting.waiting_records": "carrier",
  "underwriting.hidden": "strategist",
  "decision.approved": "carrier",
  "decision.counteroffer": "strategist",
  "decision.postponed": "strategist",
  "decision.declined": "strategist",
  "ended.withdrawn": "strategist",
  "ended.not_taken": "strategist",
  "ended.free_look": "strategist",
  "issued.delivery": "you",
  "active.in_force": "strategist",
  "active.review_booked": "strategist",
  "policy.lapsed": "strategist",
  "policy.surrendered": "strategist",
  "policy.matured": "strategist",
  "policy.claim_paid": "strategist",
  "missed.call": "coordinator",
  "closed.not_a_fit": "coordinator",
  "closed.not_going_ahead": "you",
  "closed.do_not_contact": "you",
  "closed.file": "strategist",
};

/* ------------------------------------------------------------------ */
/* buildJourney                                                        */
/* ------------------------------------------------------------------ */

export function buildJourney(facts: JourneyFacts, now: number = Date.now()): Journey {
  const appts = facts.appointments;
  const next = upcoming(appts, now);
  const app = facts.application;
  const policy = facts.policy;

  const reqs = sortRequirements(facts.requirements.map(requirementView).filter((r): r is RequirementView => !!r));
  // First premium is its own step; keep it out of the underwriting checklist.
  const underwritingReqs = reqs.filter((r) => r.kind !== "first_premium");
  const open = underwritingReqs.filter((r) => r.state === "needed");
  const booked = underwritingReqs.filter((r) => r.state === "scheduled");
  const waiting = underwritingReqs.filter((r) => r.state === "waiting");

  const introDone = latestDone(appts, "intro", now);
  const strategyDone = latestDone(appts, "strategy", now);
  const nextIntro = next.find((a) => a.kind === "intro") ?? null;
  const nextStrategy = next.find((a) => a.kind === "strategy") ?? null;
  const nextReview = next.find((a) => a.kind === "review") ?? null;

  const appPos = app ? positionForApplication(app.status, open.length > 0, open.length === 0 && booked.length > 0, open.length === 0 && booked.length === 0 && waiting.length > 0) : null;
  const appLeads = !!(app && appPos && (!policy || applicationIsNewer(app, policy)));

  let pos: Position;
  if (policy && !appLeads) {
    pos = policyPosition(policy, !!nextReview);
  } else if (app && appPos) {
    pos = appPos;
    if (pos.status === "preapp.received" && facts.intake?.status !== "submitted" && facts.visibility.intake) {
      pos = { current: "pre_application", status: "preapp.open" };
    }
    if (pos.status === "ended.withdrawn") {
      const stop = withdrawnStop(app);
      pos = { ...pos, current: stop, stoppedAt: stop };
    }
    if (pos.status === "active.in_force" && nextReview) pos = { ...pos, status: "active.review_booked" };
  } else if (app && !appPos && positionForContactStage(facts.contactStage)) {
    // An application status this app doesn't know yet: trust the contact stage over a guess.
    pos = positionForContactStage(facts.contactStage)!;
  } else if (facts.intake?.status === "submitted") {
    pos = { current: "submitted", status: "preapp.received" };
  } else if (facts.intake && facts.intake.status !== "void") {
    pos = { current: "pre_application", status: "preapp.open" };
  } else if (facts.presentation?.status === "presented" || facts.presentation?.status === "updated_after_presentation") {
    pos = { current: "pre_application", status: "plan.ready" };
  } else if (facts.presentation || strategyDone) {
    pos = { current: "plan_presented", status: "plan.preparing" };
  } else if (nextStrategy) {
    pos = { current: "strategy_session", status: "strategy.booked" };
  } else if (introDone) {
    pos = { current: "strategy_session", status: "strategy.not_booked" };
  } else if (nextIntro) {
    pos = { current: "intro_call", status: "intro.booked" };
  } else {
    pos = positionForContactStage(facts.contactStage) ?? { current: "intro_call", status: "intro.not_booked" };
  }

  // A missed call with nothing rebooked: say so, kindly.
  const lastPast = appts.filter((a) => new Date(a.startsAt).getTime() < now).sort((a, b) => b.startsAt.localeCompare(a.startsAt))[0];
  const missed = facts.contactStage === "no_show" || lastPast?.status === "no_show" || !!facts.server?.recover;
  if (missed && !app && !policy && next.length === 0 && (pos.status === "intro.not_booked" || pos.status === "strategy.not_booked")) {
    pos = { current: pos.current, status: "missed.call" };
  }

  // Stages that close the file. Finer facts (an application, a policy) win, except a closed file.
  const closed = facts.contactStage ? CLOSED_STAGES[facts.contactStage] : undefined;
  if (closed && (closed === "closed.file" || (!app && !policy))) {
    pos = { current: pos.current, status: closed, stoppedAt: pos.current };
  }

  // Coarse "somewhere in underwriting" from the contact stage alone.
  if (!app && !policy && facts.contactStage === "app_submitted" && pos.status === "plan.ready") {
    pos = positionForContactStage("app_submitted")!;
  }

  const currentIndex = STEPS.indexOf(pos.current);
  const stoppedIndex = pos.stoppedAt ? STEPS.indexOf(pos.stoppedAt) : -1;
  const hadRequirements = underwritingReqs.length > 0;

  const stepAt: Partial<Record<StepKey, string | null>> = {
    intro_call: (introDone ?? nextIntro)?.startsAt ?? null,
    strategy_session: (strategyDone ?? nextStrategy)?.startsAt ?? null,
    plan_presented: facts.presentation?.presentedAt ?? null,
    pre_application: facts.intake?.submittedAt ?? null,
    submitted: app?.submittedAt ?? null,
    decision: app?.approvedAt ?? (pos.current === "decision" ? app?.statusChangedAt ?? null : null),
    issued: app?.issuedAt ?? policy?.issuedAt ?? null,
    first_premium: policy?.firstPremiumPaidAt ?? policy?.firstPremiumDueAt ?? null,
    active: policy?.effectiveDate ?? app?.placedAt ?? null,
    annual_review: nextReview?.startsAt ?? null,
  };

  const steps: JourneyStep[] = STEPS.map((key, i) => {
    let state: StepState;
    if (stoppedIndex >= 0) {
      state = i < stoppedIndex ? "done" : i === stoppedIndex ? "stopped" : "closed";
    } else if (i < currentIndex) {
      state = "done";
    } else if (i === currentIndex) {
      state = "current";
    } else {
      state = "upcoming";
    }
    if (key === "requirements" && state === "done" && !hadRequirements) state = "skipped";
    return { key, chapter: CHAPTER_OF[key], state, at: stepAt[key] ?? null };
  });

  const doneCount = steps.filter((s) => s.state === "done" || s.state === "skipped").length;
  const progress = pos.status.startsWith("active.") ? 1 : Math.min(1, (doneCount + (stoppedIndex >= 0 ? 0 : 0.5)) / STEPS.length);

  const firstPremium = reqs.find((r) => r.kind === "first_premium");
  const effective = policy?.effectiveDate ?? app?.placedAt ?? null;

  let moment: Moment | null = null;
  if (pos.status === "decision.approved") moment = "approved";
  else if (pos.status === "issued.delivery") moment = "issued";
  else if (pos.status === "active.in_force" && effective && now - new Date(effective).getTime() < 45 * 86_400_000) moment = "active";

  return {
    steps,
    current: pos.current,
    status: pos.status,
    tone: TONE[pos.status],
    actor: pos.status === "underwriting.requirements_booked" ? bookedActor(booked) : pos.status === "underwriting.requirements_open" ? openActor(open) : ACTOR[pos.status],
    progress,
    requirements: pos.current === "requirements" || pos.status === "issued.delivery" ? reqs : [],
    moment,
    nextAppointment: next[0] ?? null,
    carrierName: app?.carrierName ?? null,
    productName: app?.productName ?? null,
    dates: {
      postponedUntil: app?.postponedUntil ?? null,
      firstPremiumDue: policy?.firstPremiumDueAt ?? firstPremium?.dueAt ?? null,
      reviewAround: nextReview?.startsAt ?? (effective ? addMonths(effective, 12) : null),
      decidedAt: app?.statusChangedAt ?? null,
    },
    counteroffer: app?.counteroffer ?? null,
    approximate: !!pos.approximate,
    recover: next.length === 0 ? (facts.server?.recover ?? null) : null,
  };
}
